/**
 * Central event date/time helper.
 *
 * The API returns `event.date` as a real UTC instant (`…T16:30:00.000Z`), and
 * the events themselves always happen in Ankara. Reading that instant with the
 * plain `getHours()` / `getDay()` family resolves it against whatever timezone
 * the *runtime* happens to be in — UTC inside the container during SSR, Europe/
 * Istanbul in a Turkish visitor's browser — so the same card rendered two
 * different clock times and Googlebot only ever saw the UTC one.
 *
 * Everything below pins the conversion to Ankara instead, so the card, the
 * calendar strip and the Event JSON-LD all describe the same moment.
 */

const EVENT_TIME_ZONE = "Europe/Istanbul";

/** Weblate keys, indexed the way Intl reports the weekday. */
const DAY_NAME_KEYS = [
  "calendar-day-sunday",
  "calendar-day-monday",
  "calendar-day-tuesday",
  "calendar-day-wednesday",
  "calendar-day-thursday",
  "calendar-day-friday",
  "calendar-day-saturday",
];

const MONTH_NAME_KEYS = [
  "calendar-month-january",
  "calendar-month-february",
  "calendar-month-march",
  "calendar-month-april",
  "calendar-month-may",
  "calendar-month-june",
  "calendar-month-july",
  "calendar-month-august",
  "calendar-month-september",
  "calendar-month-october",
  "calendar-month-november",
  "calendar-month-december",
];

/** The caller passes the `t` it already holds; this module has none. */
type Translate = (key: string, options?: Record<string, any>) => string;

const zonedFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: EVENT_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  weekday: "short",
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
};

/** Wall-clock fields of `date` as they read on a clock in Ankara. */
function getZonedParts(date: string | Date): ZonedParts | null {
  const parsed = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsed.getTime())) return null;

  const parts = zonedFormatter.formatToParts(parsed);
  const read = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    year: Number(read("year")),
    month: Number(read("month")),
    day: Number(read("day")),
    hour: Number(read("hour")),
    minute: Number(read("minute")),
    second: Number(read("second")),
    weekday: WEEKDAY_INDEX[read("weekday")] ?? 0,
  };
}

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Ankara's UTC offset *for that instant*, derived from the zoned wall clock
 * rather than hard-coded, so a future DST rule change can't silently shift
 * every startDate by an hour.
 */
function getZoneOffset(date: Date, parts: ZonedParts) {
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  const offsetMinutes = Math.round((asUtc - date.getTime()) / 60000);
  const sign = offsetMinutes < 0 ? "-" : "+";
  const absolute = Math.abs(offsetMinutes);

  return `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}

/**
 * `2026-08-30T16:30:00.000Z` → `2026-08-30T19:30:00+03:00`.
 *
 * The same instant, written with Ankara's offset instead of `Z`. Google reads
 * a bare `Z` literally, so an event that starts at 19:30 in Ankara was being
 * advertised as starting three hours early.
 */
export function getEventStartDate(date: string | Date) {
  const parsed = date instanceof Date ? date : new Date(date);
  const parts = getZonedParts(parsed);

  if (!parts) return undefined;

  const day = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  const time = `${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;

  return `${day}T${time}${getZoneOffset(parsed, parts)}`;
}

/** `19:30` — the hour the event starts, as read in Ankara. */
const getHourByDate = (date: string) => {
  const parts = getZonedParts(date);

  if (!parts) return "";

  return `${pad(parts.hour)}:${pad(parts.minute)}`;
};

const getDayByDate = (date: string, t: Translate) => {
  const parts = getZonedParts(date);

  return parts ? t(DAY_NAME_KEYS[parts.weekday]) : "";
};

const getMonthByDate = (date: string, t: Translate) => {
  const parts = getZonedParts(date);

  return parts ? t(MONTH_NAME_KEYS[parts.month - 1]) : "";
};

const getDayNumberByDate = (date: string) => {
  const parts = getZonedParts(date);

  return parts ? parts.day : "";
};

export {
  getDayByDate,
  getMonthByDate,
  getDayNumberByDate,
  getHourByDate,
  EVENT_TIME_ZONE,
};
